import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { usePublicBanners } from '@/hooks';

interface SiteBannerCarouselProps {
  position?: string;
  autoPlayInterval?: number;
  className?: string;
  compact?: boolean;
}

export function SiteBannerCarousel({
  position = 'home',
  autoPlayInterval = 5000,
  className,
  compact = false,
}: SiteBannerCarouselProps) {
  const { t } = useTranslation('student');
  const { data: banners, isLoading } = usePublicBanners(position);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const items = Array.isArray(banners) ? banners : [];

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  }, [items.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  }, [items.length]);

  // Auto-play
  useEffect(() => {
    if (!isHovered && items.length > 1) {
      const interval = setInterval(goToNext, autoPlayInterval);
      return () => clearInterval(interval);
    }
  }, [isHovered, items.length, autoPlayInterval, goToNext]);

  // Reset index when items change
  useEffect(() => {
    setCurrentIndex(0);
  }, [items.length]);

  if (isLoading || items.length === 0) return null;

  const currentItem = items[currentIndex];

  const Wrapper = currentItem.linkUrl ? 'a' : 'div';
  const wrapperProps = currentItem.linkUrl
    ? {
        href: currentItem.linkUrl,
        target: '_blank' as const,
        rel: 'noopener noreferrer',
      }
    : {};

  return (
    <div
      className={cn(
        'group relative w-full overflow-hidden',
        compact
          ? 'h-[140px] sm:h-[160px]'
          : 'h-[220px] sm:h-[320px] md:h-[400px] lg:h-[480px]',
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Wrapper
        {...wrapperProps}
        className="relative block h-full w-full cursor-pointer"
      >
        <img
          src={currentItem.imageUrl}
          alt={currentItem.title || t('cards.banner')}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
          fetchPriority="high"
          decoding="async"
          loading="eager"
        />

        {/* Subtle gradient at bottom */}
        <div className="absolute inset-0 bg-linear-to-t from-black/50 via-transparent to-transparent" />

        {/* Title overlay */}
        {currentItem.title && !compact && (
          <div className="absolute right-0 bottom-0 left-0 p-6 sm:p-8 md:p-10">
            <h2 className="text-xl font-bold text-white drop-shadow-lg sm:text-3xl md:text-4xl">
              {currentItem.title}
            </h2>
          </div>
        )}
      </Wrapper>

      {/* Navigation Arrows */}
      {items.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.preventDefault();
              goToPrevious();
            }}
            className="absolute top-1/2 left-3 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-all group-hover:opacity-100 hover:bg-black/75 sm:left-5 sm:h-12 sm:w-12"
            aria-label={t('cards.previous')}
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              goToNext();
            }}
            className="absolute top-1/2 right-3 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-all group-hover:opacity-100 hover:bg-black/75 sm:right-5 sm:h-12 sm:w-12"
            aria-label={t('cards.next')}
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </>
      )}

      {/* Dots */}
      {items.length > 1 && (
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={cn(
                'h-2 rounded-full transition-all',
                index === currentIndex
                  ? 'w-8 bg-white'
                  : 'w-2 bg-white/50 hover:bg-white/75',
              )}
              aria-label={t('cards.goToSlide', { number: index + 1 })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
